import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TbX } from 'react-icons/tb';
import { Drawer } from 'vaul';
import { Action, State as QueryFilters, createQueryFilters } from '../Hooks/useQueryFilters';
import { usePropertiesQuery } from '../Repositories/usePropertiesQuery';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Dialog } from './Dialog';
import { Input } from './Input';
import { Label } from './Label';
import { SRPMap } from './SRPMap';

interface Props {
  state: QueryFilters;
  dispatch: React.Dispatch<Action>;
}

export function SRPFiltersBar({ state, dispatch }: Props) {
  const { t } = useTranslation('search');
  const { isLoading, data } = usePropertiesQuery(createQueryFilters(state));

  const { register, handleSubmit, reset } = useForm<{
    minPrice: number;
    maxPrice: number;
    minSurface: number;
    maxSurface: number;
    minRooms: number;
    maxRooms: number;
    minBathrooms: number;
    maxBathrooms: number;
    isNew: boolean;
    isExcellent: boolean;
    isGood: boolean;
    isToBeRefurbished: boolean;
  }>({
    defaultValues: {
      ...state,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    dispatch({
      type: 'APPLY_FILTER',
      payload: { ...data, searchByMap: false },
    });
  });

  return (
    <div className="px-5 border-b border-t bg-white border-zinc-200 shadow-sm sticky top-[96px] z-[1]">
      <div className="h-[70px] flex items-center justify-between gap-5">
        {isLoading && <div>Loading...</div>}

        {data && (
          <div className="space-y-1.5">
            <ol className="flex items-center whitespace-nowrap min-w-0" aria-label="Breadcrumb">
              {data.topLocation.region && (
                <li className="text-sm text-gray-600 last:font-semibold last:text-gray-800 hidden sm:inline-block after:px-2 last:after:hidden after:content-['/']">
                  {data.topLocation.region.name}
                </li>
              )}

              {data.topLocation.province && (
                <li className="text-sm last:font-semibold last:text-gray-800 text-gray-600 hidden sm:block after:px-2 last:after:hidden after:content-['/']">
                  {data.topLocation.province.name}
                </li>
              )}

              {data.topLocation.city && (
                <li className="text-sm last:font-semibold last:text-gray-800 truncate after:px-2 last:after:hidden after:content-['/']">
                  {data.topLocation.city.name}
                </li>
              )}
            </ol>

            <div className="font-semibold text-sm">
              {t('search.results', { total: data.total })}
            </div>
          </div>
        )}

        <div className="space-x-3">
          <Drawer.Root dismissible={false}>
            <Drawer.Trigger asChild>
              <Button variant="secondary" className="inline lg:hidden">
                {t('search.map')}
              </Button>
            </Drawer.Trigger>
            <Drawer.Portal>
              <Drawer.Overlay className="fixed inset-0 bg-black/40" />
              <Drawer.Content className="bg-zinc-100 flex flex-col rounded-t-2xl h-[90%] mt-24 fixed bottom-0 left-0 right-0">
                <div className="font-semibold flex items-center justify-center h-16 border-b border-zinc-200 shadow z-20">
                  {t('search.map')}
                  <Drawer.Close>
                    <button
                      className="absolute text-xl h-10 w-10 rounded-lg inline-flex items-center justify-center top-3 right-3 hover:bg-zinc-200"
                      aria-label="Close"
                    >
                      <TbX size={24} />
                    </button>
                  </Drawer.Close>
                </div>

                <div className="absolute inset-0 top-16">
                  <SRPMap mini={true} state={state} dispatch={dispatch} />
                </div>
              </Drawer.Content>
            </Drawer.Portal>
          </Drawer.Root>

          <Dialog
            trigger={<Button>{t('search.filters')}</Button>}
            content={(setIsOpen) => (
              <form onSubmit={onSubmit} className="flex flex-col justify-between relative">
                <div className="h-full space-y-5">
                  <div>
                    <h3 className="text-lg leading-none font-semibold text-neutral-800">
                      {t('filters.title')}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-neutral-500">
                      {t('filters.subtitle')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.minPrice')}</Label>
                      <Input type="number" {...register('minPrice')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.maxPrice')}</Label>
                      <Input type="number" {...register('maxPrice')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.minSurface')}</Label>
                      <Input type="number" {...register('minSurface')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.maxSurface')}</Label>
                      <Input type="number" {...register('maxSurface')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.minRooms')}</Label>
                      <Input type="number" {...register('minRooms')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.maxRooms')}</Label>
                      <Input type="number" {...register('maxRooms')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.minBathrooms')}</Label>
                      <Input type="number" {...register('minBathrooms')} />
                    </div>
                    <div className="flex flex-col space-y-1.5">
                      <Label>{t('filters.items.maxBathrooms')}</Label>
                      <Input type="number" {...register('maxBathrooms')} />
                    </div>

                    <div className="col-span-2">
                      <div className="font-semibold text-neutral-800">
                        {t('filters.items.status')}
                      </div>

                      <div className="space-y-3 mt-3">
                        <div className="flex items-center gap-3">
                          <Checkbox id="isNew" {...register('isNew')} />
                          <Label htmlFor="isNew">{t('filters.items.isNew')}</Label>
                        </div>
                        <div className="flex items-center gap-3">
                          <Checkbox id="isExcellent" {...register('isExcellent')} />
                          <Label htmlFor="isExcellent">{t('filters.items.isExcellent')}</Label>
                        </div>
                        <div className="flex items-center gap-3">
                          <Checkbox id="isGood" {...register('isGood')} />
                          <Label htmlFor="isGood">{t('filters.items.isGood')}</Label>
                        </div>
                        <div className="flex items-center gap-3">
                          <Checkbox id="isToBeRefurbished" {...register('isToBeRefurbished')} />
                          <Label htmlFor="isToBeRefurbished">
                            {t('filters.items.isToBeRefurbished')}
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 py-6 mt-1 sticky bottom-0 bg-white">
                  <Button
                    type="submit"
                    onClick={() => setIsOpen(false)}
                    className="w-full justify-center"
                  >
                    {t('filters.actions.apply')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => reset(undefined, { keepDefaultValues: false })}
                    className="w-full justify-center"
                  >
                    {t('filters.actions.cancel')}
                  </Button>
                </div>
              </form>
            )}
          />
        </div>
      </div>
    </div>
  );
}
